import { expandTutorDeskGuideCatalog } from './guide-catalog.js';
import { TUTOR_DESK_GUIDE_UI_COPY, TUTOR_DESK_GUIDE_DIALOG_COPY } from './guide-content-copy.js';

export const TUTOR_DESK_GUIDE_TEXT_STRUCTURE_VERSION = '1';
export const TUTOR_DESK_GUIDE_CONTENT_SCHEMA_VERSION = '1.0.0';
export const TUTOR_DESK_GUIDE_CONTENT_LOCALE = 'en-CA';
export const TUTOR_DESK_GUIDE_CONTENT_MAX_BYTES = 512 * 1024;
export const TUTOR_DESK_GUIDE_CONTENT_TARGETS = Object.freeze(['saas-staging', 'saas-production']);
const KEY_PATTERN = /^(?:task|dialog|prepare|ui)\.[a-zA-Z0-9][a-zA-Z0-9._-]{0,220}$/u;
const HASH_PATTERN = /^[a-f0-9]{64}$/u;
const PROHIBITED_KEYS = new Set(['__proto__', 'prototype', 'constructor']);
const INVALID_TEXT = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f\u202a-\u202e\u2066-\u2069]|<\s*\/?\s*[a-z!]|javascript\s*:/iu;

function error(code, message, status = 400) {
  return Object.assign(new Error(message), { code, status });
}

function plainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
    && [Object.prototype, null].includes(Object.getPrototypeOf(value));
}

function placeholders(value = '') {
  return [...String(value).matchAll(/\{([a-zA-Z][a-zA-Z0-9]*)\}/gu)].map((match) => match[1]).sort();
}

function fieldLabel(field) {
  return ({ title: 'Title', summary: 'Short description', what: 'What to do', why: 'Why it matters', expectedResult: 'Expected result' })[field] || field;
}

export function createTutorDeskGuideContentRegistry(catalog = {}) {
  const entries = [];
  const seen = new Set();
  const add = (key, defaultValue, { groupId, groupLabel, label, maxLength = 4000 } = {}) => {
    if (!KEY_PATTERN.test(key) || seen.has(key) || typeof defaultValue !== 'string') {
      throw error('guide_registry_invalid', `Guide text registry contains an invalid or duplicate key: ${key}`);
    }
    seen.add(key);
    entries.push(Object.freeze({ key, groupId, groupLabel, label, defaultValue, maxLength, placeholders: Object.freeze(placeholders(defaultValue)) }));
  };
  const expanded = expandTutorDeskGuideCatalog(catalog);
  for (const task of expanded.tasks || []) {
    // These structural fallbacks always render a specific dialog or contextual
    // template. Do not offer catalog prose that never reaches the screen.
    if (['context.modal-review', 'context.menu-review'].includes(task.taskId)) continue;
    const groupId = `task.${task.taskId}`;
    const groupLabel = String(task.title || task.taskId);
    for (const field of ['title', 'summary']) add(`${groupId}.${field}`, String(task[field] || ''), {
      groupId, groupLabel, label: fieldLabel(field), maxLength: field === 'title' ? 180 : 1000,
    });
    for (const [index, step] of (task.steps || []).entries()) {
      for (const field of ['what', 'why', 'expectedResult']) add(`${groupId}.step.${step.stepId}.${field}`, String(step[field] || ''), {
        groupId, groupLabel, label: `Step ${index + 1} — ${fieldLabel(field)}`,
      });
      if (typeof step.recovery?.message === 'string') add(`${groupId}.step.${step.stepId}.recovery`, step.recovery.message, {
        groupId, groupLabel, label: `Step ${index + 1} — Recovery explanation`,
      });
    }
  }
  for (const [id, profile] of Object.entries(TUTOR_DESK_GUIDE_DIALOG_COPY)) {
    const groupId = `dialog.${id}`;
    for (const field of ['title', 'summary', 'what', 'why', 'expectedResult']) add(`${groupId}.${field}`, profile[field], {
      groupId, groupLabel: `${profile.matchTitle} window`, label: fieldLabel(field), maxLength: field === 'title' ? 180 : 4000,
    });
  }
  for (const [key, definition] of Object.entries(TUTOR_DESK_GUIDE_UI_COPY)) {
    add(key, typeof definition === 'string' ? definition : definition.text, {
      groupId: definition.groupId || (key.startsWith('prepare.') ? 'preparation' : 'guide-controls'),
      groupLabel: definition.groupLabel || (key.startsWith('prepare.') ? 'Before you start' : 'Guide controls and messages'),
      label: definition.label || key.split('.').at(-1).replaceAll('-', ' '),
      maxLength: definition.maxLength || 4000,
    });
  }
  entries.sort((left, right) => left.key.localeCompare(right.key, 'en'));
  const defaults = Object.freeze(Object.fromEntries(entries.map((entry) => [entry.key, entry.defaultValue])));
  return Object.freeze({
    schemaVersion: TUTOR_DESK_GUIDE_CONTENT_SCHEMA_VERSION,
    appId: 'tutor-desk',
    locale: TUTOR_DESK_GUIDE_CONTENT_LOCALE,
    textStructureVersion: TUTOR_DESK_GUIDE_TEXT_STRUCTURE_VERSION,
    entries: Object.freeze(entries),
    defaults,
  });
}

function validatePlainText(value, key, limit = 4000) {
  if (typeof value !== 'string' || !value.trim() || value.length > limit || INVALID_TEXT.test(value)) {
    throw error('guide_content_text_invalid', `Guide text is invalid or too long for ${key}.`);
  }
  // Lone UTF-16 surrogates cannot round-trip consistently through UTF-8 digests.
  if (typeof value.isWellFormed === 'function' && !value.isWellFormed()) {
    throw error('guide_content_text_invalid', `Guide text contains invalid Unicode for ${key}.`);
  }
  return value;
}

export function validateTutorDeskGuideContentStrings(strings, registry, {
  allowUnknown = false,
  fillDefaults = false,
} = {}) {
  if (!plainObject(strings) || !Array.isArray(registry?.entries)) throw error('guide_content_invalid', 'A Guide text object is required.');
  const descriptors = Object.getOwnPropertyDescriptors(strings);
  const entries = new Map(registry.entries.map((entry) => [entry.key, entry]));
  if (Reflect.ownKeys(strings).some((key) => typeof key !== 'string' || PROHIBITED_KEYS.has(key))) {
    throw error('guide_content_key_invalid', 'Guide text contains an unsupported key.');
  }
  const result = fillDefaults ? { ...registry.defaults } : {};
  for (const [key, descriptor] of Object.entries(descriptors)) {
    if (!Object.hasOwn(descriptor, 'value') || !descriptor.enumerable || !KEY_PATTERN.test(key)) {
      throw error('guide_content_key_invalid', 'Guide text contains an unsupported key.');
    }
    const entry = entries.get(key);
    if (!entry && !allowUnknown) throw error('guide_content_unknown_key', `This app does not support the Guide text field ${key}.`);
    const value = validatePlainText(descriptor.value, key, entry?.maxLength || 4000);
    if (!entry) continue;
    if (JSON.stringify(placeholders(value)) !== JSON.stringify(entry.placeholders)) {
      throw error('guide_content_placeholder_invalid', `Keep the required placeholders in ${entry.label}.`);
    }
    // Braces may only contain approved complete placeholders, never a second
    // interpolation language or unmatched expression.
    if (/[{}]/u.test(value.replace(/\{[a-zA-Z][a-zA-Z0-9]*\}/gu, ''))) {
      throw error('guide_content_placeholder_invalid', `Guide text has an incomplete placeholder in ${entry.label}.`);
    }
    result[key] = value;
  }
  if (!fillDefaults && registry.entries.some((entry) => !Object.hasOwn(result, entry.key))) {
    throw error('guide_content_incomplete', 'Save the complete current Guide text document.');
  }
  if (new TextEncoder().encode(stableTutorDeskGuideContentJson(strings)).byteLength > TUTOR_DESK_GUIDE_CONTENT_MAX_BYTES) {
    throw error('guide_content_too_large', 'The Guide text document is too large.', 413);
  }
  return result;
}

function stableValue(value) {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(Object.keys(value).sort().map((key) => [key, stableValue(value[key])]));
}

export function stableTutorDeskGuideContentJson(value) {
  return JSON.stringify(stableValue(value));
}

export async function hashTutorDeskGuideContent(value) {
  const bytes = new TextEncoder().encode(stableTutorDeskGuideContentJson(value));
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function tutorDeskGuideRegistryDigest(registry) {
  return hashTutorDeskGuideContent({
    schemaVersion: registry.schemaVersion,
    appId: registry.appId,
    locale: registry.locale,
    textStructureVersion: registry.textStructureVersion,
    entries: registry.entries,
  });
}

export async function validateTutorDeskGuideContentSnapshot(payload, registry, { target } = {}) {
  const value = plainObject(payload?.data) ? payload.data : payload;
  if (!plainObject(value) || value.schemaVersion !== TUTOR_DESK_GUIDE_CONTENT_SCHEMA_VERSION
    || value.appId !== 'tutor-desk' || !TUTOR_DESK_GUIDE_CONTENT_TARGETS.includes(target) || value.target !== target
    || value.locale !== registry.locale || value.textStructureVersion !== registry.textStructureVersion
    || !HASH_PATTERN.test(value.registryDigest || '') || !HASH_PATTERN.test(value.contentSha256 || '')
    || !Number.isSafeInteger(value.revision) || value.revision < 0 || typeof value.published !== 'boolean'
    || (value.published && value.revision < 1) || (!value.published && value.revision !== 0)) {
    throw error('guide_content_snapshot_invalid', 'The Guide text response does not match this app and environment.');
  }
  const strings = validateTutorDeskGuideContentStrings(value.strings, registry, { allowUnknown: true, fillDefaults: true });
  if (await hashTutorDeskGuideContent(value.strings) !== value.contentSha256) {
    throw error('guide_content_digest_invalid', 'The Guide text response could not be verified.');
  }
  return Object.freeze({ ...value, strings: Object.freeze(strings) });
}

export function formatTutorDeskGuideText(registry, strings, key, values = {}) {
  const template = strings?.[key] ?? registry?.defaults?.[key];
  if (typeof template !== 'string') throw error('guide_content_key_missing', `Guide text is missing: ${key}`);
  return template.replace(/\{([a-zA-Z][a-zA-Z0-9]*)\}/gu, (_, name) => {
    if (!Object.hasOwn(values, name)) throw error('guide_content_value_missing', `Guide text is missing a value for ${key}.`);
    return String(values[name] ?? '');
  });
}

export function defaultTutorDeskGuideText(key, values = {}) {
  const entry = TUTOR_DESK_GUIDE_UI_COPY[key];
  const template = typeof entry === 'string' ? entry : entry?.text;
  if (typeof template !== 'string') throw error('guide_content_key_missing', `Guide text is missing: ${key}`);
  return template.replace(/\{([a-zA-Z][a-zA-Z0-9]*)\}/gu, (_, name) => {
    if (!Object.hasOwn(values, name)) throw error('guide_content_value_missing', `Guide text is missing a value for ${key}.`);
    return String(values[name] ?? '');
  });
}

export function presentTutorDeskGuideTask(task, registry, strings) {
  if (!task) return task;
  if (['context.modal-review', 'context.menu-review'].includes(task.taskId)) return task;
  const read = (key) => formatTutorDeskGuideText(registry, strings, key);
  const prefix = `task.${task.taskId}`;
  return {
    ...task,
    title: read(`${prefix}.title`),
    summary: read(`${prefix}.summary`),
    steps: (task.steps || []).map((step) => ({
      ...step,
      what: read(`${prefix}.step.${step.stepId}.what`),
      why: read(`${prefix}.step.${step.stepId}.why`),
      expectedResult: read(`${prefix}.step.${step.stepId}.expectedResult`),
      ...(typeof step.recovery?.message === 'string' ? {
        recovery: { ...step.recovery, message: read(`${prefix}.step.${step.stepId}.recovery`) },
      } : {}),
    })),
  };
}
